import { useEffect, useRef, useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/base/PageHeader";
import { StatusBadge } from "@/components/base/StatusBadge";
import { LoadingState } from "@/components/base/LoadingState";
import { EmptyState } from "@/components/base/EmptyState";
import { Modal } from "@/components/base/Modal";
import { ConfirmDialog } from "@/components/base/ConfirmDialog";
import { IntegrationsNav } from "@/components/feature/IntegrationsNav";
import { fmtDateTime } from "@/lib/format";
import { Link, useSearchParams } from "react-router-dom";
import {
  registerConnector,
  testConnectorConnection,
  type ConnectorRow,
} from "@/lib/integrations";
import { providerRegistry, providerMeta, marketplaceSiteLabel } from "@/lib/integrations/registry";
import {
  lastDomainSuccessAtByConnector,
  lastSuccessfulJobAtByConnector,
  presentConnectorStatus,
} from "@/lib/connector-status-ux";
import { merchantConnectionActions } from "@/lib/merchant-connection-status";
import { connectorDisplayName } from "@/lib/connector-identity";
import {
  applyMarketplaceSelection,
  connectorOnboardingCopy,
  defaultConnectorDisplayName,
  emptyConnectorOnboardingDraft,
  oauthFunctionName,
  validateConnectorOnboarding,
  type ConnectorOnboardingDraft,
} from "@/lib/connector-onboarding";
import { connectorDraftStorageKey } from "@/lib/setup-draft-storage";
import { useSetupDraft } from "@/hooks/useSetupDraft";
import { useCreateDeepLink } from "@/hooks/useCreateDeepLink";
import { ConnectorDefaultPrice } from "@/pages/integrations/components/ConnectorDefaultPrice";
import { invalidateConnectorQueries } from "@/lib/connector-queries";
import { useConnectorRowSubscription } from "@/hooks/useConnectorRowSubscription";
import {
  AUTOMATIC_SYNC_SCOPE_NOTE,
  AUTOMATIC_SYNC_STALE_WARNING,
  connectorIdsWithLiveListings,
  setConnectorAutomaticSync,
  shouldWarnAutomaticSyncOff,
} from "@/lib/connector-automatic-sync";
import { createConnectorPendingGuard } from "@/lib/catalogue-sync-job-selection";

const inputCls =
  "w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500";
const labelCls = "block text-xs font-medium text-slate-500 mb-1";

function healthDot(tone: "positive" | "neutral" | "warning" | "danger") {
  const color =
    tone === "positive"
      ? "bg-emerald-500"
      : tone === "danger"
        ? "bg-red-500"
        : tone === "warning"
          ? "bg-amber-400"
          : "bg-slate-300";
  return <span className={`w-2 h-2 rounded-full ${color}`} />;
}

function friendlyConnectorLine(c: ConnectorRow): string {
  const meta = providerMeta(c.provider);
  const identity = connectorDisplayName({
    id: c.id,
    name: c.name,
    provider: c.provider,
    authStatus: c.auth_status,
    sellerUsername: c.provider === "ebay" ? c.external_account_id : null,
    storeHash: c.provider === "bigcommerce" ? c.external_account_id : null,
    storeName: c.provider === "bigcommerce" ? c.name : null,
  });
  const site = c.marketplace_site ? marketplaceSiteLabel(c.marketplace_site) : "";
  return [identity, meta?.label, site].filter(Boolean).join(" · ");
}

export default function Connectors() {
  const { org, role } = useAuth();
  const queryClient = useQueryClient();
  useConnectorRowSubscription(org?.id);
  const isAdmin = role === "owner" || role === "admin";
  const [searchParams, setSearchParams] = useSearchParams();

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [feedback, setFeedback] = useState("");
  const [busyIds, setBusyIds] = useState<ReadonlySet<string>>(() => new Set());
  const pendingGuardRef = useRef(createConnectorPendingGuard());
  const syncBusyUi = (id: string) => busyIds.has(id) || pendingGuardRef.current.isPending(id);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ConnectorRow | null>(null);
  const [draft, setDraft, clearDraft] = useSetupDraft<ConnectorOnboardingDraft>(
    connectorDraftStorageKey(org?.id),
    { ...emptyConnectorOnboardingDraft("ebay"), open: false },
  );

  useCreateDeepLink(() => setDraft((d) => ({ ...d, open: true })));

  useEffect(() => {
    const result = searchParams.get("oauth_result");
    const provider = searchParams.get("oauth_provider");
    if (!result) return;
    const message = searchParams.get("oauth_message");
    if (result === "success") {
      setFeedback(`${provider === "ebay" ? "eBay" : provider === "bigcommerce" ? "BigCommerce" : "Marketplace"} account connected.`);
    } else if (result === "app_not_configured") {
      setFeedback(message || "This marketplace app is not configured in Tradivo yet.");
    } else {
      setFeedback(message || "Connection failed. You can try Connect again.");
    }
    setSearchParams({}, { replace: true });
    invalidateConnectorQueries(queryClient, org?.id);
  }, [searchParams, setSearchParams, queryClient, org?.id]);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["connectors", org?.id],
    enabled: !!org?.id,
    queryFn: async () => {
      const [connectorsRes, jobsRes, liveListingsRes] = await Promise.all([
        supabase
          .from("connectors")
          .select(
            "id, name, type, provider, config, status, enabled, health_status, auth_status, connection_status, external_account_id, marketplace_site, capabilities, credential_ref, connection_error, last_sync_at, last_sync_attempt_at, auth_attempted_at, created_at",
          )
          .eq("org_id", org!.id)
          .order("created_at", { ascending: true }),
        supabase
          .from("sync_jobs")
          .select("connector_id, completed_at, status")
          .eq("org_id", org!.id)
          .in("status", ["succeeded", "partially_failed"])
          .not("completed_at", "is", null),
        // Lightweight live-listing presence for Automatic Sync stale warning,
        // plus the only domain-specific completed-success evidence available
        // through this existing read contract: a listing that was actually
        // published. Generic sync_jobs/last_sync_at never establish this.
        supabase
          .from("channel_listings")
          .select("connector_id, last_published_at")
          .eq("org_id", org!.id)
          .eq("publication_status", "published")
          .not("status", "in", "(archived,ended)"),
      ]);
      const connectors = (connectorsRes.data ?? []) as ConnectorRow[];
      const lastJobAt = lastSuccessfulJobAtByConnector(jobsRes.data ?? []);
      const liveListingConnectorIds = connectorIdsWithLiveListings(liveListingsRes.data ?? []);
      const domainSuccessAt = lastDomainSuccessAtByConnector(liveListingsRes.data ?? []);
      return { connectors, lastJobAt, liveListingConnectorIds, domainSuccessAt };
    },
  });

  const activeMeta = providerMeta(draft.provider);
  const copy = connectorOnboardingCopy(draft.provider);
  const showSites = Boolean(copy.siteLabel);

  const groups = providerRegistry
    .map((p) => ({ meta: p, items: (data?.connectors ?? []).filter((c) => c.provider === p.provider) }))
    .filter((g) => g.items.length > 0);

  const handleProviderChange = (provider: string) => {
    setDraft((f) => applyMarketplaceSelection(f, provider));
  };

  const startProviderOAuth = async (provider: string, connectorId: string) => {
    const fn = oauthFunctionName(provider);
    if (!fn) {
      setError("This marketplace is not available for self-service connection yet.");
      return false;
    }
    const res = await supabase.functions.invoke(fn, {
      body: { connector_id: connectorId, org_id: org!.id },
    });
    const payload = res.data as { success?: boolean; authorization_url?: string; error?: string; code?: string } | null;
    if (payload?.code === "APP_NOT_CONFIGURED" || payload?.error?.includes("not configured")) {
      setError(payload.error || "Marketplace app installation is not configured yet.");
      return false;
    }
    if (res.error || !payload?.success || !payload.authorization_url) {
      throw new Error(payload?.error || res.error?.message || "Failed to start authorization");
    }
    window.location.assign(payload.authorization_url);
    return true;
  };

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    const validation = validateConnectorOnboarding(draft);
    if (validation) {
      setError(validation);
      return;
    }
    setSaving(true);
    try {
      const res = await registerConnector({
        provider: draft.provider,
        name: defaultConnectorDisplayName(draft.provider, draft.marketplace_site, draft.name),
        orgId: org!.id,
        marketplace_site: draft.marketplace_site || null,
      });
      if (!res.ok || !res.id) {
        setError(res.error ?? "Unable to register connector.");
        setSaving(false);
        return;
      }
      clearDraft();
      invalidateConnectorQueries(queryClient, org?.id);
      await startProviderOAuth(draft.provider, res.id);
    } catch (err) {
      setError((err as Error).message);
    }
    setSaving(false);
  };

  const toggleEnabled = async (c: ConnectorRow) => {
    if (!org?.id || !isAdmin) return;
    const outcome = await pendingGuardRef.current.run(c.id, async () => {
      setBusyIds(new Set(pendingGuardRef.current.pendingIds()));
      setError("");
      setFeedback("");
      const nextEnabled = !c.enabled;
      try {
        const result = await setConnectorAutomaticSync(
          supabase as unknown as Parameters<typeof setConnectorAutomaticSync>[0],
          {
            orgId: org.id,
            connectorId: c.id,
            enabled: nextEnabled,
          },
        );
        if (result.ok === false) {
          setError(result.error);
          return;
        }
        invalidateConnectorQueries(queryClient, org.id);
      } finally {
        setBusyIds(new Set(pendingGuardRef.current.pendingIds()));
      }
    });
    if (outcome === "skipped") return;
    setBusyIds(new Set(pendingGuardRef.current.pendingIds()));
  };

  const testConnection = async (c: ConnectorRow) => {
    setTestingId(c.id);
    setFeedback("");
    try {
      const res = await testConnectorConnection(c.id);
      if (res.ok) {
        setFeedback(`${c.name}: connection verified.`);
      } else {
        setFeedback(`${c.name}: ${res.state === "not_configured" ? "requires authorization" : res.error ?? "connection failed"}.`);
      }
    } catch (err) {
      setFeedback(`${c.name}: ${(err as Error).message}`);
    }
    setTestingId(null);
    invalidateConnectorQueries(queryClient, org?.id);
  };

  const reconnect = async (c: ConnectorRow) => {
    const outcome = await pendingGuardRef.current.run(c.id, async () => {
      setBusyIds(new Set(pendingGuardRef.current.pendingIds()));
      setError("");
      try {
        await startProviderOAuth(c.provider || "", c.id);
      } catch (err) {
        setFeedback(`${c.name}: ${(err as Error).message}`);
      } finally {
        setBusyIds(new Set(pendingGuardRef.current.pendingIds()));
      }
    });
    if (outcome === "skipped") return;
    setBusyIds(new Set(pendingGuardRef.current.pendingIds()));
  };

  const removeConnector = async () => {
    if (!deleteTarget || !org?.id) return;
    const id = deleteTarget.id;
    const orgId = org.id;
    const outcome = await pendingGuardRef.current.run(id, async () => {
      setBusyIds(new Set(pendingGuardRef.current.pendingIds()));
      setError("");
      try {
        const { data, error: disconnectError } = await supabase.rpc("disconnect_connector", {
          p_org_id: orgId,
          p_connector_id: id,
        });
        if (disconnectError) {
          setError(disconnectError.message);
          return;
        }
        if (!(data as { success?: boolean } | null)?.success) {
          setError((data as { error?: string } | null)?.error ?? "Could not disconnect this connector.");
          return;
        }
        setDeleteTarget(null);
        invalidateConnectorQueries(queryClient, orgId);
      } finally {
        setBusyIds(new Set(pendingGuardRef.current.pendingIds()));
      }
    });
    if (outcome === "skipped") return;
    setBusyIds(new Set(pendingGuardRef.current.pendingIds()));
  };

  return (
    <div>
      <PageHeader title="Integrations" subtitle="Connect marketplaces and carriers — each store is its own connection">
        {isAdmin && (
          <button
            onClick={() => {
              setError("");
              setDraft((d) => ({ ...d, open: true }));
            }}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2"
          >
            <i className="ri-add-line" /> Add Connector
          </button>
        )}
      </PageHeader>

      {!isAdmin && (
        <div className="mb-4 px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-500 flex items-center gap-2">
          <i className="ri-shield-check-line text-slate-400" />
          You can view integrations, but only administrators can connect or manage them.
        </div>
      )}

      {error && !draft.open && (
        <div
          className="mb-4 px-4 py-3 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700 flex items-center gap-2"
          role="alert"
          data-testid="automatic-sync-error"
        >
          <i className="ri-error-warning-line text-red-500" />
          {error}
        </div>
      )}

      {feedback && (
        <div className="mb-4 px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700 flex items-center gap-2">
          <i className="ri-information-line text-slate-400" />
          {feedback}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          {isLoading ? (
            <div className="bg-white rounded-xl border border-slate-200 p-8">
              <LoadingState />
            </div>
          ) : isError ? (
            <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
              <p className="text-sm text-slate-500 mb-4">Couldn't load connectors.</p>
              <button
                onClick={() => refetch()}
                className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg whitespace-nowrap"
              >
                Retry
              </button>
            </div>
          ) : groups.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200">
              <EmptyState
                icon="ri-plug-line"
                title="No connectors yet"
                description="Add a connector to connect a marketplace store — each store is an independent connection."
              />
            </div>
          ) : (
            <div className="space-y-8">
              {groups.map((group) => (
                <div key={group.meta.provider}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center">
                      <i className={`${group.meta.icon} text-slate-600 text-lg`} />
                    </div>
                    <h3 className="text-base font-semibold text-slate-800">{group.meta.label}</h3>
                    <span className="text-xs text-slate-400">{group.items.length} store{group.items.length === 1 ? "" : "s"}</span>
                  </div>
                  <div className="space-y-3">
                    {group.items.map((c) => {
                      const view = presentConnectorStatus(c, {
                        lastDomainSuccessAt: data?.domainSuccessAt.get(c.id) ?? null,
                        lastActivityAt: data?.lastJobAt.get(c.id) ?? null,
                      });
                      const actions = merchantConnectionActions(c);
                      const hasLiveListings = data?.liveListingConnectorIds.has(c.id) ?? false;
                      const showStaleWarning = shouldWarnAutomaticSyncOff({
                        enabled: c.enabled,
                        hasLiveListings,
                      });
                      const syncBusy = syncBusyUi(c.id);
                      return (
                      <div key={c.id} className="bg-white rounded-xl border border-slate-200 p-5">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-3 min-w-0">
                            <div className="w-10 h-10 rounded-lg bg-slate-50 border border-slate-100 flex items-center justify-center shrink-0">
                              <i className={`${group.meta.icon} text-slate-500 text-lg`} />
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h4 className="text-sm font-semibold text-slate-800">{c.name}</h4>
                                <StatusBadge
                                  status={view.authorization.badge}
                                  customLabel={view.authorization.label}
                                />
                                <StatusBadge
                                  status={view.automaticSync.badge}
                                  customLabel={view.automaticSync.label}
                                />
                              </div>
                              <p className="text-xs text-slate-500 mt-1">
                                {friendlyConnectorLine(c)}
                              </p>
                              {view.attention.required && (
                                <p className="text-xs text-amber-600 mt-1.5 flex items-center gap-1">
                                  <i className="ri-alert-line" />
                                  {view.attention.message}
                                  {view.attention.actionLabel ? ` · ${view.attention.actionLabel}` : ""}
                                </p>
                              )}
                              {showStaleWarning && (
                                <p
                                  className="text-xs text-amber-700 mt-1.5 flex items-start gap-1"
                                  data-testid={`automatic-sync-stale-warning-${c.id}`}
                                >
                                  <i className="ri-alert-line mt-0.5 shrink-0" />
                                  {AUTOMATIC_SYNC_STALE_WARNING}
                                </p>
                              )}
                              {isAdmin && (c.provider === "ebay" || c.provider === "bigcommerce") && (
                                <ConnectorDefaultPrice
                                  orgId={org!.id}
                                  connector={c}
                                  onSaved={() => invalidateConnectorQueries(queryClient, org?.id)}
                                />
                              )}
                              <div className="flex items-center gap-3 mt-2 text-xs text-slate-400 flex-wrap">
                                <span
                                  className="flex items-center gap-1.5"
                                  data-testid={`connector-health-${c.id}`}
                                >
                                  {healthDot(view.health.tone)}
                                  {view.health.label}
                                </span>
                                <span data-testid={`connector-domain-sync-${c.id}`}>
                                  {view.lastSuccessfulSyncAt
                                    ? `${view.lastSuccessfulSyncCaption} ${fmtDateTime(view.lastSuccessfulSyncAt)}`
                                    : view.lastSuccessfulSyncUnavailableLabel}
                                </span>
                                {view.lastActivityAt && (
                                  <span data-testid={`connector-activity-${c.id}`}>
                                    {view.lastActivityCaption} {fmtDateTime(view.lastActivityAt)}
                                  </span>
                                )}
                              </div>
                              {isAdmin && (
                                <div className="mt-3 flex items-center gap-3">
                                  <span className="text-xs font-medium text-slate-600">Automatic sync</span>
                                  <button
                                    type="button"
                                    role="switch"
                                    aria-checked={c.enabled}
                                    aria-label={c.enabled ? "Turn Automatic Sync off" : "Turn Automatic Sync on"}
                                    title={c.enabled ? "Turn Automatic Sync off" : "Turn Automatic Sync on"}
                                    data-testid={`automatic-sync-toggle-${c.id}`}
                                    onClick={() => void toggleEnabled(c)}
                                    disabled={syncBusy}
                                    className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40 disabled:opacity-60 ${
                                      c.enabled ? "bg-emerald-600" : "bg-slate-300"
                                    }`}
                                  >
                                    <span
                                      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                                        c.enabled ? "translate-x-6" : "translate-x-1"
                                      }`}
                                    />
                                  </button>
                                  <span className="text-xs font-semibold text-slate-700 tabular-nums" aria-live="polite">
                                    {syncBusy ? "Saving…" : c.enabled ? "ON" : "OFF"}
                                  </span>
                                </div>
                              )}
                              {isAdmin && (
                                <p
                                  className="text-[11px] text-slate-400 mt-1.5 max-w-md"
                                  data-testid={`automatic-sync-scope-${c.id}`}
                                >
                                  {AUTOMATIC_SYNC_SCOPE_NOTE}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0 flex-wrap justify-end">
                            {isAdmin && actions.includes("connect") && (
                              <button
                                onClick={() => reconnect(c)}
                                disabled={syncBusyUi(c.id)}
                                className="px-3 py-1.5 text-xs font-medium rounded-lg text-emerald-700 bg-emerald-50 hover:bg-emerald-100 whitespace-nowrap"
                              >
                                Connect
                              </button>
                            )}
                            {isAdmin && actions.includes("reconnect") && (
                              <button
                                onClick={() => reconnect(c)}
                                disabled={syncBusyUi(c.id)}
                                className="px-3 py-1.5 text-xs font-medium rounded-lg text-emerald-700 bg-emerald-50 hover:bg-emerald-100 whitespace-nowrap"
                              >
                                Reconnect
                              </button>
                            )}
                            {isAdmin && (c.provider === "bigcommerce" || c.provider === "ebay") && (
                              <Link
                                to={`/integrations/${c.provider}/${c.id}`}
                                className="px-3 py-1.5 text-xs font-medium rounded-lg text-emerald-700 bg-emerald-50 hover:bg-emerald-100 whitespace-nowrap flex items-center gap-1.5"
                              >
                                <i className="ri-settings-3-line" />
                                Configure
                              </Link>
                            )}
                            {isAdmin && actions.includes("test") && (
                              <button
                                onClick={() => testConnection(c)}
                                disabled={testingId === c.id}
                                className="px-3 py-1.5 text-xs font-medium rounded-lg text-slate-600 hover:bg-slate-100 whitespace-nowrap flex items-center gap-1.5"
                              >
                                <i className={`ri-radar-line ${testingId === c.id ? "animate-spin" : ""}`} />
                                {testingId === c.id ? "Testing…" : "Test connection"}
                              </button>
                            )}
                            {isAdmin && actions.includes("disconnect") && (
                              <button
                                onClick={() => setDeleteTarget(c)}
                                disabled={syncBusyUi(c.id)}
                                className="px-3 py-1.5 text-xs font-medium rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-500 whitespace-nowrap"
                                title="Disconnect"
                              >
                                Disconnect
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <IntegrationsNav />
      </div>

      <Modal
        open={draft.open}
        onClose={() => setDraft((d) => ({ ...d, open: false }))}
        title="Add Connector"
        footer={
          <>
            <button
              onClick={() => setDraft((d) => ({ ...d, open: false }))}
              className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={saving || copy.connectability !== "oauth"}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap"
            >
              {saving ? "Connecting…" : copy.connectActionLabel}
            </button>
          </>
        }
      >
        <form onSubmit={handleCreate} className="space-y-4">
          {error && <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{error}</div>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className={labelCls}>Marketplace *</label>
              <select value={draft.provider} onChange={(e) => handleProviderChange(e.target.value)} className={inputCls}>
                {providerRegistry.map((p) => (
                  <option key={p.provider} value={p.provider}>
                    {p.label}
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-slate-400 mt-1">{copy.description}</p>
            </div>
            {showSites && (
              <div className="sm:col-span-2">
                <label className={labelCls}>{copy.siteLabel} {copy.siteRequired ? "*" : ""}</label>
                <select
                  value={draft.marketplace_site}
                  onChange={(e) => setDraft((f) => ({ ...f, marketplace_site: e.target.value }))}
                  className={inputCls}
                >
                  <option value="">Select site…</option>
                  {activeMeta?.marketplaceSites.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
                {copy.siteHelp && <p className="text-[11px] text-slate-400 mt-1">{copy.siteHelp}</p>}
              </div>
            )}
            <div className="sm:col-span-2">
              <label className={labelCls}>{copy.displayNameLabel}</label>
              <input
                value={draft.name}
                onChange={(e) => setDraft((f) => ({ ...f, name: e.target.value }))}
                className={inputCls}
                placeholder={copy.displayNamePlaceholder}
              />
              <p className="text-[11px] text-slate-400 mt-1">{copy.displayNameHelp}</p>
            </div>
            <div className="sm:col-span-2">
              <p className="text-[11px] text-slate-500">{copy.helpText}</p>
            </div>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Disconnect"
        message={`Disconnect "${deleteTarget?.name ?? ""}"? This stops Tradivo syncing with this account and revokes its authorization. Imported orders, products, and inventory are kept.`}
        confirmLabel="Disconnect"
        loading={!!deleteTarget && syncBusyUi(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={removeConnector}
      />
    </div>
  );
}
